import * as THREE from 'three';
import circleImg from '../../assets/images/img/circle.png';
import crossImg from '../../assets/images/img/cross.png';
import diamondImg from '../../assets/images/img/diamond.png';
import hexagonImg from '../../assets/images/img/hexagon.png';
import snowImg from '../../assets/images/img/Snow.png';
import squareImg from '../../assets/images/img/Square.png';
import starImg from '../../assets/images/img/Star.png';
import triangleImg from '../../assets/images/img/triangle.png';

/** Shape id → bundled PNG (webpack resolves to URL). */
export const PARTICLE_SHAPE_IMAGES = {
  circle: circleImg,
  cross: crossImg,
  diamond: diamondImg,
  hexagon: hexagonImg,
  snow: snowImg,
  square: squareImg,
  star: starImg,
  triangle: triangleImg,
};

/** Default shape per particle type when none is stored in Redux/scene. */
export const DEFAULT_PARTICLE_SHAPE_BY_TYPE = {
  fire: 'fire',
  rain: 'rain',
  snow: 'snow',
  stars: 'star',
  star: 'star',
};

export function getDefaultParticleShape(type) {
  const key = String(type || '').toLowerCase();
  return DEFAULT_PARTICLE_SHAPE_BY_TYPE[key] || 'circle';
}

export const PARTICLE_SHAPE_OPTIONS = [
  { value: 'circle', label: 'Circle' },
  { value: 'square', label: 'Square' },
  { value: 'triangle', label: 'Triangle' },
  { value: 'star', label: 'Star' },
  { value: 'diamond', label: 'Diamond' },
  { value: 'cross', label: 'Cross' },
  { value: 'hexagon', label: 'Hexagon' },
  { value: 'snow', label: 'Snow' },
  { value: 'fire', label: 'Fire (Special)' },
  { value: 'rain', label: 'Rain (Special)' },
];

const preloadedImages = {};

Object.entries(PARTICLE_SHAPE_IMAGES).forEach(([name, src]) => {
  const img = new Image();
  img.decoding = 'async';
  img.src = src;
  preloadedImages[name] = img;
});

const isImageReady = (img) =>
  img && img.complete && img.naturalWidth > 0;

const parseCssColor = (color) => {
  const c = new THREE.Color(color);
  return {
    r: Math.round(c.r * 255),
    g: Math.round(c.g * 255),
    b: Math.round(c.b * 255),
  };
};

/**
 * Shape PNGs are white silhouettes on opaque black (no alpha channel).
 * Use luminance as alpha so particles are not full squares.
 */
function applyLuminanceAlpha(ctx, size, tintColor = null) {
  const imageData = ctx.getImageData(0, 0, size, size);
  const data = imageData.data;
  const tint = tintColor ? parseCssColor(tintColor) : null;

  for (let i = 0; i < data.length; i += 4) {
    const lum = Math.max(data[i], data[i + 1], data[i + 2]);
    if (lum < 8) {
      data[i + 3] = 0;
      continue;
    }
    const alpha = lum / 255;
    if (tint) {
      data[i] = tint.r;
      data[i + 1] = tint.g;
      data[i + 2] = tint.b;
    }
    data[i + 3] = Math.round(255 * alpha);
  }

  ctx.putImageData(imageData, 0, 0);
}

/** Procedural fire gradient (no PNG). */
function drawFireShape(ctx, size) {
  const centerX = size / 2;
  const centerY = size / 2;
  const radius = size / 2 - 2;
  const fireGradient = ctx.createRadialGradient(
    centerX,
    centerY,
    0,
    centerX,
    centerY,
    radius
  );
  fireGradient.addColorStop(0, '#ffffff');
  fireGradient.addColorStop(0.4, '#ffc800');
  fireGradient.addColorStop(0.7, '#ff6400');
  fireGradient.addColorStop(1, '#ff0000');
  ctx.fillStyle = fireGradient;
  ctx.beginPath();
  ctx.arc(centerX, centerY, radius, 0, Math.PI * 2);
  ctx.fill();
}

/** Procedural thin vertical rain streak (white + alpha; tint via material). */
function drawRainShape(ctx, size) {
  const centerX = size / 2;
  const streakWidth = Math.max(1.25, size * 0.045);
  const tipY = size * 0.12;
  const endY = size * 0.88;
  const midY = size * 0.5;

  const glow = ctx.createRadialGradient(
    centerX,
    midY,
    0,
    centerX,
    midY,
    size * 0.42
  );
  glow.addColorStop(0, '#ffffff33');
  glow.addColorStop(1, '#ffffff00');
  ctx.fillStyle = glow;
  ctx.beginPath();
  ctx.ellipse(centerX, midY, size * 0.12, size * 0.4, 0, 0, Math.PI * 2);
  ctx.fill();

  const lineGrad = ctx.createLinearGradient(centerX, tipY, centerX, endY);
  lineGrad.addColorStop(0, '#ffffff00');
  lineGrad.addColorStop(0.18, '#ffffffaa');
  lineGrad.addColorStop(0.5, '#ffffffff');
  lineGrad.addColorStop(0.82, '#ffffffaa');
  lineGrad.addColorStop(1, '#ffffff00');

  ctx.strokeStyle = lineGrad;
  ctx.lineWidth = streakWidth;
  ctx.lineCap = 'round';
  ctx.beginPath();
  ctx.moveTo(centerX, tipY);
  ctx.lineTo(centerX, endY);
  ctx.stroke();

  ctx.strokeStyle = '#ffffff88';
  ctx.lineWidth = Math.max(0.75, streakWidth * 0.35);
  ctx.beginPath();
  ctx.moveTo(centerX, size * 0.28);
  ctx.lineTo(centerX, size * 0.72);
  ctx.stroke();
}

/** Fallback soft circle when PNG is not loaded yet. */
function drawFallbackCircle(ctx, size, color) {
  const centerX = size / 2;
  const centerY = size / 2;
  const radius = size / 2 - 2;
  const gradient = ctx.createRadialGradient(
    centerX,
    centerY,
    0,
    centerX,
    centerY,
    radius
  );
  gradient.addColorStop(0, color);
  gradient.addColorStop(0.7, color + '80');
  gradient.addColorStop(1, color + '00');
  ctx.fillStyle = gradient;
  ctx.beginPath();
  ctx.arc(centerX, centerY, radius, 0, Math.PI * 2);
  ctx.fill();
}

/**
 * Draw a shape PNG; black background becomes transparent, white is tinted.
 */
function drawImageShape(ctx, img, size, color) {
  ctx.clearRect(0, 0, size, size);
  ctx.drawImage(img, 0, 0, size, size);
  applyLuminanceAlpha(ctx, size, color);
}

function drawSnowImageShape(ctx, img, size) {
  ctx.clearRect(0, 0, size, size);
  ctx.drawImage(img, 0, 0, size, size);
  applyLuminanceAlpha(ctx, size, null);
}

function resolveShapeImage(key) {
  if (isImageReady(preloadedImages[key])) {
    return preloadedImages[key];
  }
  if (isImageReady(preloadedImages.circle)) {
    return preloadedImages.circle;
  }
  return null;
}

/**
 * Create a particle sprite texture from bundled shape PNGs or procedural specials.
 * @param {string} shape
 * @param {string} color - CSS color for tinting
 * @param {number} size - canvas resolution
 * @param {number} shapeRotation - degrees
 * @param {number} shapeScale
 */
export function createParticleTexture(
  shape = 'circle',
  color = '#ffffff',
  size = 64,
  shapeRotation = 0,
  shapeScale = 1
) {
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext('2d');
  const key = String(shape).toLowerCase();
  const centerX = size / 2;
  const centerY = size / 2;

  ctx.clearRect(0, 0, size, size);
  ctx.save();
  ctx.translate(centerX, centerY);
  if (shapeRotation) {
    ctx.rotate((Number(shapeRotation) * Math.PI) / 180);
  }
  if (shapeScale !== 1) {
    ctx.scale(Number(shapeScale), Number(shapeScale));
  }
  ctx.translate(-centerX, -centerY);

  if (key === 'fire') {
    drawFireShape(ctx, size);
  } else if (key === 'rain') {
    drawRainShape(ctx, size);
  } else {
    const img = resolveShapeImage(key);

    if (img) {
      if (key === 'snow') {
        drawSnowImageShape(ctx, img, size);
      } else {
        drawImageShape(ctx, img, size, color);
      }
    } else {
      drawFallbackCircle(ctx, size, color);
    }
  }

  ctx.restore();

  const texture = new THREE.CanvasTexture(canvas);
  texture.needsUpdate = true;
  texture.premultiplyAlpha = true;
  return texture;
}

/** Replace map on PointsMaterial when shape/color/rotation/scale changes. */
export function applyParticleShapeMap(
  material,
  shape,
  color,
  shapeRotation = 0,
  shapeScale = 1,
  textureSize = 64
) {
  if (!material) return;
  if (material.map) {
    material.map.dispose();
  }
  material.map = createParticleTexture(
    shape,
    color,
    textureSize,
    shapeRotation,
    shapeScale
  );
  material.transparent = true;
  material.depthWrite = false;
  material.alphaTest = 0.01;
  material.needsUpdate = true;
}

export default createParticleTexture;
